import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Investors & Partners · Ivory Pulse";
  }, []);

  // The approved homepage remains behind the existing investor gate.
  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Unable to sign in. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505] text-white"
      data-testid="login-page"
    >
      {/* supplied background — full screen */}
      <img
        src={`${process.env.PUBLIC_URL}/login-bg.webp`}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* restrained veil for readability only */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(96deg, rgba(5,5,5,0.78) 0%, rgba(5,5,5,0.55) 34%, rgba(5,5,5,0.18) 62%, rgba(5,5,5,0.05) 100%), linear-gradient(to bottom, rgba(5,5,5,0.35) 0%, transparent 20%, transparent 82%, rgba(5,5,5,0.5) 100%)",
        }}
      />

      <div className="relative mx-auto flex min-h-[100svh] max-w-[1180px] items-center px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 0.84, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Ivory Pulse"
            draggable={false}
            className="brand-invert h-12 w-auto"
            data-testid="login-logo"
          />

          <div className="mt-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-[#E8D9B0]">
              Investors &amp; Partners
            </p>
            <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.34em] text-white/45">
              Access
            </p>
          </div>

          <form onSubmit={submit} className="mt-10" data-testid="login-form">
            <label className="block">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/40">
                Email
              </span>
              <input
                type="text"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full border-0 border-b border-white/20 bg-transparent py-2.5 text-[15px] transition-colors focus:border-[#C7A962] focus:outline-none placeholder:text-white/25"
                placeholder="Email or access ID"
              />
            </label>

            <label className="mt-7 block">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/40">
                Password
              </span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full border-0 border-b border-white/20 bg-transparent py-2.5 text-[15px] transition-colors focus:border-[#C7A962] focus:outline-none placeholder:text-white/25"
                placeholder="••••••••••"
              />
            </label>

            <div className="mt-3 flex justify-end">
              <a
                href="mailto:investors@ivory-pulse.com?subject=Password%20reset%20request"
                data-testid="login-forgot-link"
                className="text-[12px] text-white/40 transition-colors hover:text-white/75"
              >
                Forgot password?
              </a>
            </div>

            {error && (
              <p
                role="alert"
                data-testid="login-error"
                className="mt-5 text-[12.5px] leading-relaxed text-red-300/90"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="group mt-8 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-white text-[13.5px] font-semibold text-[#050505] transition-[transform,opacity] duration-300 hover:scale-[1.01] disabled:opacity-60 sm:w-auto sm:px-10"
            >
              {loading ? "Signing in…" : "Sign In"}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </form>

          <p className="mt-10 max-w-xs text-[12px] leading-relaxed text-white/40">
            Restricted access for approved Ivory Pulse investors and strategic
            partners.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
